import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Activate from "@/pages/Activate";
import Dashboard from "@/pages/Dashboard";
import MyDashboard from "@/pages/MyDashboard";
import RecordsPage from "@/pages/RecordsPage";
import TenantEntry from "@/pages/TenantEntry";
import ClientProfile from "@/pages/ClientProfile";
import Sites from "@/pages/Sites";
import Supervisions from "@/pages/Supervisions";
import Users from "@/pages/Users";
import Mobile from "@/pages/Mobile";
import Rota from "@/pages/Rota";
import FamilyPortal from "@/pages/FamilyPortal";
import PortalView from "@/pages/PortalView";
import MarChart from "@/pages/MarChart";
import PortalInbox from "@/pages/PortalInbox";
import StaffSchedule from "@/pages/StaffSchedule";
import StaffProfile from "@/pages/StaffProfile";
import CDLedger from "@/pages/CDLedger";
import DeliveryNotes from "@/pages/DeliveryNotes";
import MyShifts from "@/pages/MyShifts";
import TimeOffRequests from "@/pages/TimeOffRequests";
import Calendar from "@/pages/Calendar";
import AuditCentre from "@/pages/AuditCentre";
import { CarePlansOverview, RiskRegister } from "@/pages/CarePlansAndRisk";
import FeedbackCentre from "@/pages/FeedbackCentre";
import DataMigration from "@/pages/DataMigration";
import MigrationProject from "@/pages/MigrationProject";
import MigrationExecutive from "@/pages/MigrationExecutive";
import Outcomes from "@/pages/Outcomes";
import OutcomeDetail from "@/pages/OutcomeDetail";
import Incidents from "@/pages/Incidents";
import IncidentFull from "@/pages/IncidentFull";
import Training from "@/pages/Training";
import AuditsQA from "@/pages/AuditsQA";
import ClientCareCost from "@/pages/ClientCareCost";
import Payroll from "@/pages/Payroll";
import MedicationCompetency from "@/pages/MedicationCompetency";
import ServiceUsers from "@/pages/ServiceUsers";
import PlatformShell from "@/pages/platform/PlatformShell";
import PlatformLogin from "@/pages/platform/PlatformLogin";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformOrgs from "@/pages/platform/PlatformOrgs";
import PlatformOrgProfile from "@/pages/platform/PlatformOrgProfile";
import PlatformOnboarding from "@/pages/platform/PlatformOnboarding";
import PlatformAuditLog from "@/pages/platform/PlatformAuditLog";
import PlatformSecurity from "@/pages/platform/PlatformSecurity";
import { PlatformAuthProvider } from "@/context/PlatformAuthContext";
import { Toaster } from "@/components/ui/sonner";

export function ShellRoute({ children }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

// Route-level switch that picks the right dashboard for the signed-in user.
// Care Workers (and anyone without `dashboard.manager.view`) get MyDashboard.
// This runs *before* Dashboard.jsx renders so no manager data flashes on
// screen; the backend also gates every manager endpoint with 403 for
// defence in depth.
function RoleDashboard() {
  const { user } = useAuth();
  const MANAGER_ROLES = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"];
  const role = (user?.role || "").toLowerCase();
  const perms = user?.permissions || [];
  const isManager = MANAGER_ROLES.includes(role) || perms.includes("dashboard.manager.view");
  return isManager ? <Dashboard /> : <MyDashboard />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/activate/:token" element={<Activate />} />
            <Route path="/family/:token" element={<PortalView />} />
            <Route path="/" element={<ShellRoute><RoleDashboard /></ShellRoute>} />
            <Route path="/mobile" element={<ProtectedRoute><Mobile /></ProtectedRoute>} />
            <Route path="/rota" element={<ShellRoute><Rota /></ShellRoute>} />
            <Route path="/staff-schedule" element={<ShellRoute><StaffSchedule /></ShellRoute>} />
            <Route path="/client-care-cost" element={<ShellRoute><ClientCareCost /></ShellRoute>} />
            <Route path="/payroll" element={<ShellRoute><Payroll /></ShellRoute>} />
            <Route path="/staff/:staffId" element={<ShellRoute><StaffProfile /></ShellRoute>} />
            <Route path="/mar-chart" element={<ShellRoute><MarChart /></ShellRoute>} />
            <Route path="/cd-ledger" element={<ShellRoute><CDLedger /></ShellRoute>} />
            <Route path="/delivery-notes" element={<ShellRoute><DeliveryNotes /></ShellRoute>} />
            <Route path="/my-shifts" element={<ShellRoute><MyShifts /></ShellRoute>} />
            <Route path="/calendar" element={<ShellRoute><Calendar /></ShellRoute>} />
            <Route path="/appointments" element={<Navigate to="/calendar" replace />} />
            <Route path="/audit" element={<ShellRoute><AuditCentre /></ShellRoute>} />
            <Route path="/care-plans" element={<ShellRoute><CarePlansOverview /></ShellRoute>} />
            <Route path="/risk-register" element={<ShellRoute><RiskRegister /></ShellRoute>} />
            <Route path="/time-off-requests" element={<ShellRoute><TimeOffRequests /></ShellRoute>} />
            <Route path="/family-portal" element={<ShellRoute><FamilyPortal /></ShellRoute>} />
            <Route path="/family-inbox" element={<ShellRoute><PortalInbox /></ShellRoute>} />
            <Route path="/sites" element={<ShellRoute><Sites /></ShellRoute>} />
            <Route path="/users" element={<ShellRoute><Users /></ShellRoute>}  />
            <Route path="/clients" element={<ShellRoute><ServiceUsers /></ShellRoute>} />
            <Route path="/clients/:clientId" element={<ShellRoute><ClientProfile /></ShellRoute>} />
            <Route path="/clients/:clientId/:section" element={<ShellRoute><ClientProfile /></ShellRoute>} />
            <Route path="/complaints" element={<ShellRoute><FeedbackCentre /></ShellRoute>} />
            <Route path="/migration" element={<ShellRoute><DataMigration /></ShellRoute>} />
            <Route path="/migration/executive" element={<ShellRoute><MigrationExecutive /></ShellRoute>} />
            <Route path="/migration/:id" element={<ShellRoute><MigrationProject /></ShellRoute>} />
            <Route path="/outcomes" element={<ShellRoute><Outcomes /></ShellRoute>} />
            <Route path="/outcomes/:id" element={<ShellRoute><OutcomeDetail /></ShellRoute>} />
            <Route path="/supervisions" element={<ShellRoute><Supervisions /></ShellRoute>} />
            <Route path="/medication-competency" element={<ShellRoute><MedicationCompetency /></ShellRoute>} />
            <Route path="/incidents" element={<ShellRoute><Incidents /></ShellRoute>} />
            <Route path="/incidents/:id" element={<ShellRoute><IncidentFull /></ShellRoute>} />
            <Route path="/training" element={<ShellRoute><Training /></ShellRoute>} />
            <Route path="/records/training" element={<Navigate to="/training" replace />} />
            <Route path="/audits" element={<ShellRoute><AuditsQA /></ShellRoute>} />
            <Route path="/records/audits" element={<Navigate to="/audits" replace />} />
            {/* ---- Platform Admin (separate security context, no ShellRoute) ---- */}
            <Route path="/platform-admin/login" element={<PlatformAuthProvider><PlatformLogin /></PlatformAuthProvider>} />
            <Route path="/platform-admin" element={<PlatformAuthProvider><PlatformShell /></PlatformAuthProvider>}>
              <Route index element={<PlatformDashboard />} />
              <Route path="organisations" element={<PlatformOrgs />} />
              <Route path="organisations/:orgId" element={<PlatformOrgProfile />} />
              <Route path="onboarding" element={<PlatformOnboarding />} />
              <Route path="audit-log" element={<PlatformAuditLog />} />
              <Route path="security" element={<PlatformSecurity />} />
            </Route>
            {/* Path-based tenant routing: /:orgSlug tries tenant discovery,
                falls back to the legacy /:moduleKey behaviour if the slug is
                not a known active tenant. See TenantEntry.jsx. */}
            <Route path="/:orgSlug" element={<TenantEntry />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors closeButton duration={3000} />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
